/**
 * Provider display config + inbox-group helpers — the pure, non-the provider subset of
 * the former the former provider-utils module. Relocated as part of the broker
 * decommission (native-only connections): no provider SDK types, no server deps,
 * safe for client components.
 *
 * `getProviderDisplayName`/`getProviderColor`/`getProviderIcon` (the React-icon
 * variant) live in `@/lib/config/providers` — consumers use those. This module
 * carries the INBOX-specific display config and the inbox/group transforms.
 */
import { Inbox, InboxGroup } from "@/lib/core/crm/types";
import { Connection } from "@/lib/types/unified";

// Provider mapping for consistent transformation
const PROVIDER_MAP: Record<
  string,
  "google-mail" | "outlook-oauth" | "slack-oauth" | "professional-email"
> = {
  "google-mail": "google-mail",
  microsoft: "outlook-oauth",
  outlook: "outlook-oauth",
  slack: "slack-oauth",
  "professional-email": "professional-email",
};

// Provider configuration for consistent styling
const PROVIDER_CONFIG = {
  "google-mail": {
    name: "Gmail",
    color: "#ea4335",
    icon: "📨",
  },
  "outlook-oauth": {
    name: "Outlook",
    color: "#0078d4",
    icon: "📧",
  },
  "slack-oauth": {
    name: "Slack",
    color: "#4a154b",
    icon: "💬",
  },
  "professional-email": {
    name: "WTC Email",
    color: "#0d9488",
    icon: "✉️",
  },
};

/**
 * Transform connections (the `/api/connections/connections` row shape) to inboxes.
 */
export function transformConnectionsToInboxes(connections: Connection[]): Inbox[] {
  return connections.map((connection) => {
    const provider = PROVIDER_MAP[connection.provider] || "google-mail";
    const config = PROVIDER_CONFIG[provider];

    return {
      id: connection.connectionId,
      name: connection.email || `${config.name} Inbox`,
      provider: provider,
      connectionId: connection.connectionId,
      email: connection.email,
      unreadCount: 0,
      totalCount: 0,
      lastSyncAt: connection.lastFetchedAt,
      isActive: true,
      color: config.color,
      icon: config.icon,
    };
  });
}

/**
 * Create inbox groups from inboxes with actual inboxes included.
 */
export function createInboxGroups(inboxes: Inbox[]): (InboxGroup & { inboxes: Inbox[] })[] {
  const groups: Record<string, InboxGroup & { inboxes: Inbox[] }> = {};

  inboxes.forEach((inbox) => {
    const groupKey = inbox.provider;

    if (!groups[groupKey]) {
      const config = PROVIDER_CONFIG[groupKey as keyof typeof PROVIDER_CONFIG];
      groups[groupKey] = {
        id: groupKey as string,
        name: config.name,
        description: `${config.name} inboxes`,
        color: config.color,
        inboxIds: [],
        isCollapsed: false,
        order:
          groupKey === "professional-email"
            ? 0
            : groupKey === "google-mail"
              ? 1
              : groupKey === "outlook-oauth"
                ? 2
                : 3,
        inboxes: [],
      };
    }

    groups[groupKey].inboxIds.push(inbox.id);
    groups[groupKey].inboxes.push(inbox);
  });

  return Object.values(groups).sort((a, b) => a.order - b.order);
}

/**
 * Get provider configuration (name / color / icon) for an inbox.
 */
export function getProviderConfig(provider: string) {
  const mappedProvider = PROVIDER_MAP[provider] || "google-mail";
  return PROVIDER_CONFIG[mappedProvider];
}
